import { PlusIcon, FileText, FileCheck, Archive, Heart, MessageSquare, BarChart2, Clock } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import CommonButton from '../../../../components/commonButton';
import CustomTable from '../../../../components/table/CustomTable';
import { CommentIcon, LikeIcon } from '../../../../assets/icons';
import Axios, { authHeaders } from '../../../../utils/axios';
import { formatMomentDate } from '../../../../utils/dateHelper';
import PostView from '../../../../components/post-modals/PostView';
import DeletePopup from '../../../../components/delete-popup';
import { Dropdown } from 'react-bootstrap';
import PostCommentsModal from '../../../../components/post-modals/PostCommentsModal';
import UserNetworkingModal from '../../../../components/modals/UserNetworkingModal';
import { useToast } from '../../../../context/TostContext';
import { TableSearch } from '../../../../components/form/Fields';
import PostModal from '../../../../components/post-modals/PostModal';
import StatusChip from '../../../../components/statusChip/StatusChip';

const ImageSwiper = ({ media, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  if (!media || media.length === 0) {
    return (
      <div
        className="bg-light rounded border d-flex align-items-center justify-content-center text-muted small"
        style={{ width: "50px", height: "50px", flexShrink: 0 }}
      >
        N/A
      </div>
    );
  }

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
  };

  const currentMedia = media[currentIndex];

  return (
    <div className="position-relative rounded-3 overflow-hidden border shadow-sm" style={{ width: "50px", height: "50px", flexShrink: 0, backgroundColor: '#f8f9fa' }}>
      {currentMedia?.mimetype === "video/mp4" ?
        <video src={currentMedia.file?.url || currentMedia.url}
          alt={title || "Post Media"}
          className="w-100 h-100"
          style={{ objectFit: "cover" }} /> :
        <img
          src={currentMedia.file?.url || currentMedia.url}
          alt={title || "Post Media"}
          className="w-100 h-100"
          style={{ objectFit: "cover" }}
          onError={(e) => { e.target.src = '/images/image-placeholder.png'; }}
        />}
      {media.length > 1 && (
        <>
          <button
            onClick={handlePrev}
            className="position-absolute top-50 start-0 translate-middle-y border-0 bg-dark bg-opacity-50 text-white d-flex align-items-center justify-content-center"
            style={{ width: "15px", height: "15px", borderRadius: "0 2px 2px 0", cursor: "pointer", fontSize: "10px", padding: 0 }}
          >
            &#10094;
          </button>
          <button
            onClick={handleNext}
            className="position-absolute top-50 end-0 translate-middle-y border-0 bg-dark bg-opacity-50 text-white d-flex align-items-center justify-content-center"
            style={{ width: "15px", height: "15px", borderRadius: "2px 0 0 2px", cursor: "pointer", fontSize: "10px", padding: 0 }}
          >
            &#10095;
          </button>
          <div className="position-absolute bottom-0 start-50 translate-middle-x d-flex gap-1 mb-1">
            {media.map((_, idx) => (
              <div
                key={idx}
                className="rounded-circle"
                style={{
                  width: "3px",
                  height: "3px",
                  backgroundColor: currentIndex === idx ? "white" : "rgba(255,255,255,0.5)"
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────
   Visibility chip colour map
   ───────────────────────────────────────── */
const visibilityChipMap = {
  'public': 'success',
  'friends': 'warning',
  'only_me': 'danger',
  'Public': 'success',
  'Friends': 'warning',
  'Only Me': 'danger',
};


/* ─────────────────────────────────────────
   Component
   ───────────────────────────────────────── */
const Posts = () => {
  const { id } = useParams();
  const { companyDetail } = useSelector((state) => state.company);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const toast = useToast();
  const [postsList, setPostsList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [postStats, setPostStats] = useState({
    total: 0,
    active: 0,
    scheduled: 0,
    inactive: 0,
    total_likes: 0,
    total_comments: 0
  });
  const [statsLoading, setStatsLoading] = useState(false);

  // Modal state
  const [showPostModal, setShowPostModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [postMediaType, setPostMediaType] = useState("image");
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);

  const [showPostViewModal, setShowPostViewModal] = useState(false);
  const [selectedPostForView, setSelectedPostForView] = useState(null);

  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState(null);

  const [showLikesModal, setShowLikesModal] = useState(false);
  const [selectedPostIdForLikes, setSelectedPostIdForLikes] = useState(null);

  const columns = React.useMemo(() => [
    {
      id: 'content',
      header: 'CONTENT',
      minWidth: '300px',
      cell: (_, row) => {
        const rawContent = row?.content || "";

        // Convert closing paragraph tags and br tags to newlines, then strip remaining HTML tags
        const cleanHtml = rawContent.replace(/<\/p>/gi, '\n').replace(/<br\s*\/?>/gi, '\n');
        const plainText = cleanHtml.replace(/<[^>]*>/g, '').trim();

        // Split by newline and filter out empty lines to get actual line count
        const linesArray = plainText.split('\n').filter(line => line.trim() !== "");
        const lineCount = linesArray.length;
        const charCount = plainText.length;

        // Show "Show More" if lines exceed 3 OR character count exceeds 100 (which wraps to >3 lines at 220px width)
        const hasMore = lineCount > 3 || charCount > 100;

        return (
          <div className="d-flex align-items-center gap-3">
            <ImageSwiper media={row.media} title={row.content} />
            {plainText ? (
              <div
                className='overflow-hidden cursor-pointer'
                onClick={() => {
                  setSelectedPostForView(row);
                  setShowPostViewModal(true);
                }}
              >
                <div
                  className="mb-1 fw-semibold text-dark"
                  style={{
                    maxWidth: '220px',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word'
                  }}
                  dangerouslySetInnerHTML={{ __html: rawContent }}
                />
                {hasMore && (
                  <span className="text-primary small fw-semibold">
                    Show More
                  </span>
                )}
              </div>
            ) : (
              <span className="text-muted small">—</span>
            )}
          </div>
        );
      }
    },
    {
      id: 'visibility',
      header: 'VISIBILITY',
      cell: (_, row) => {
        const label = row.visibility || 'public';
        return (
          <StatusChip
            bg={visibilityChipMap[label] || 'success'}
            pill
            title={label.toUpperCase().replace('_', ' ')}
          />
        );
      }
    },
    {
      id: 'status',
      header: 'STATUS',
      cell: (_, row) => {
        if (row.status === 'scheduled') {
          return (
            <StatusChip
              bg="info"
              pill
              title="Scheduled"
            />
          );
        }
        return (
          <StatusChip
            bg={row.status === 'active' ? 'success' : 'danger'}
            pill
            title={row.status === 'active' ? 'Active' : 'Inactive'}
          />
        );
      },
    },
    {
      id: 'scheduledAt',
      header: 'SCHEDULED FOR',
      cell: (_, row) => {
        if (row.status === 'scheduled' && row.scheduled_at) {
          return (
            <span className="text-muted small">
              {formatMomentDate(row.scheduled_at, "DD-MM-YYYY hh:mm A")}
            </span>
          );
        }
        return <span className="text-muted">—</span>;
      }
    },
    {
      id: 'engagement',
      header: 'ENGAGEMENT',
      cell: (_, row) => {
        const likes = row.reactions?.total || row.likeCount || 0;
        const comments = row.commentCount || 0;
        return (
          <div className="d-flex align-items-center gap-3 text-nowrap text-muted">
            <span
              title="Likes"
              style={{ cursor: likes > 0 ? "pointer" : "default" }}
              onClick={(e) => {
                e.stopPropagation();
                if (likes > 0) {
                  setSelectedPostIdForLikes(row.id);
                  setShowLikesModal(true);
                }
              }}
            >
              <LikeIcon size={16} className="me-2" /> {likes}
            </span>
            <span
              title="Comments"
              style={{ cursor: comments > 0 ? "pointer" : "default" }}
              onClick={(e) => {
                e.stopPropagation();
                if (comments > 0) {
                  setSelectedPostId(row.id);
                  setShowCommentsModal(true);
                }
              }}
            >
              <CommentIcon size={16} className="me-2" /> {comments}
            </span>
          </div>
        );
      },
    },
    {
      id: 'createdAt',
      header: 'CREATED AT',
      cell: (_, row) => formatMomentDate(row.created_at, "DD-MM-YYYY hh:mm A")
    },
    {
      id: 'updatedAt',
      header: 'UPDATED AT',
      cell: (_, row) => formatMomentDate(row.updated_at, "DD-MM-YYYY hh:mm A")
    },
  ], []);

  const fetchPosts = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await Axios.get(`/companies/${id}/posts`, {
        params: {
          page: currentPage,
          limit: pageSize,
          search: searchTerm
        },
        ...authHeaders()
      });
      const result = response.data?.data;
      setPostsList(result?.data || result?.rows || result || []);
      setTotal(response.data?.meta?.total || response.data?.data?.meta?.total || 0);
    } catch (error) {
      console.error("Failed to fetch posts:", error);
    } finally {
      setLoading(false);
    }
  }, [id, currentPage, pageSize, searchTerm]);

  const fetchPostStats = useCallback(async () => {
    if (!id) return;
    setStatsLoading(true);
    try {
      const response = await Axios.get(`/companies/${id}/posts/stats`, {
        ...authHeaders()
      });
      if (response.data?.data) {
        setPostStats(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch post stats:", error);
    } finally {
      setStatsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    fetchPostStats();
  }, [fetchPostStats]);

  const totalPages = Math.ceil(total / pageSize);
  const activePage = Math.min(currentPage, Math.max(1, totalPages));

  const handlePostSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      if (selectedPost) {
        await Axios.patch(`/companies/${id}/posts/${selectedPost.id}`, formData, {
          ...authHeaders(),
          headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' }
        });
        toast.success("Post updated successfully");
      } else {
        await Axios.post(`/companies/${id}/posts`, formData, {
          ...authHeaders(),
          headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' }
        });
        toast.success("Post created successfully");
      }
      setShowPostModal(false);
      fetchPosts();
      fetchPostStats();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePost = async () => {
    if (!postToDelete) return;
    setIsSubmitting(true);
    try {
      await Axios.delete(`/companies/${id}/posts/${postToDelete}`, {
        ...authHeaders()
      });
      toast.success("Post deleted successfully");
      setShowDeletePopup(false);
      setPostToDelete(null);
      fetchPosts();
      fetchPostStats();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to delete post");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePublishNow = async (postId) => {
    setIsSubmitting(true);
    try {
      await Axios.post(`/companies/${id}/posts/${postId}/publish-now`, {}, {
        ...authHeaders()
      });
      toast.success("Post published successfully");
      fetchPosts();
      fetchPostStats();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to publish post");
    } finally {
      setIsSubmitting(false);
    }
  };

  const actionList = [
    { title: 'View', handler: (row) => { setSelectedPostForView(row); setShowPostViewModal(true); } },
    { title: 'Edit', handler: (row) => { setSelectedPost(row); setShowPostModal(true); } },
    {
      title: 'Publish Now',
      handler: (row) => handlePublishNow(row.id),
      isVisible: (row) => row.status === 'scheduled',
    },
    { title: 'Delete', handler: (row) => { setPostToDelete(row.id); setShowDeletePopup(true); }, isDanger: true },
  ];

  return (
    <div className="grid gap-3">
      {/* List Header */}
      <div className="bg-white rounded-2 overflow-hidden common-shadow g-col-9">
        <div className="p-4 d-flex align-items-center justify-content-between gap-4">
          <TableSearch value={searchTerm}
            placeholder="Search"
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }} />

          <Dropdown align="end">
            <Dropdown.Toggle as={React.forwardRef(({ children, onClick }, ref) => (
              <div ref={ref} onClick={(e) => { e.preventDefault(); onClick(e); }}>
                <CommonButton
                  icon={<PlusIcon />}
                  variant="primary"
                  title="Create Post"
                />
              </div>
            ))} id="dropdown-create-post" />
            <Dropdown.Menu style={{ zIndex: 9999 }}>
              <Dropdown.Item
                onClick={() => {
                  setSelectedPost(null);
                  setPostMediaType("image");
                  setShowPostModal(true);
                }}
                as="button"
              >
                Image Post
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => {
                  setSelectedPost(null);
                  setPostMediaType("video");
                  setShowPostModal(true);
                }}
                as="button"
              >
                Video Post
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

        </div>
        <div className="overflow-hidden">
          <CustomTable
            data={postsList}
            columns={columns}
            keyField="id"
            actionList={actionList}
            emptyMessage="No posts found"
            loading={loading}
            rowClick={(row) => {
              setSelectedPostForView(row);
              setShowPostViewModal(true);
            }}
            tbody={{ trClass: "cursor-pointer" }}
            cover={{ mainClass: "user-posts-table" }}
            pagination={{
              currentPage: activePage,
              totalPages,
              total,
              onPageChange: (page) => setCurrentPage(page),
              pageSize,
              setPageSize: (size) => {
                setPageSize(size);
                setCurrentPage(1);
              },
              setCurrentPage,
            }}
          />
        </div>
      </div>
      {/* other details */}
      <div className="g-col-3 bg-white rounded-2 common-shadow p-4 align-self-start">
        {/* Post Overview Title */}
        <div className="d-flex align-items-center gap-2 mb-4">
          <BarChart2 size={20} className="text-primary" />
          <h6 className="mb-0 fw-semibold text-primary">Post Overview</h6>
        </div>

        {/* Metrics list */}
        <div className="d-flex flex-column gap-3">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <FileText size={18} className="text-secondary" />
              <span className='fw-medium text-muted fs-md'>Total Posts</span>
            </div>
            <span className="fw-semibold text-primary fs-md">
              {statsLoading ? '...' : postStats.total.toLocaleString()}
            </span>
          </div>

          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <FileCheck size={18} className="text-secondary" />
              <span className='fw-medium text-muted fs-md'>Active Posts</span>
            </div>
            <span className="fw-semibold text-primary fs-md">
              {statsLoading ? '...' : postStats.active.toLocaleString()}
            </span>
          </div>

          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <Clock size={18} className="text-secondary" />
              <span className='fw-medium text-muted fs-md'>Scheduled Posts</span>
            </div>
            <span className="fw-semibold text-primary fs-md">
              {statsLoading ? '...' : postStats.scheduled.toLocaleString()}
            </span>
          </div>

          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <Archive size={18} className="text-secondary" />
              <span className='fw-medium text-muted fs-md'>Inactive Posts</span>
            </div>
            <span className="fw-semibold text-primary fs-md">
              {statsLoading ? '...' : postStats.inactive.toLocaleString()}
            </span>
          </div>

          <hr className="my-2" style={{ borderTop: '1px solid #E4E6EF', opacity: 1 }} />

          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <Heart size={18} className="text-secondary" />
              <span className='fw-medium text-muted fs-md'>Total Likes</span>
            </div>
            <span className="fw-semibold text-primary fs-md">
              {statsLoading ? '...' : postStats.total_likes.toLocaleString()}
            </span>
          </div>

          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <MessageSquare size={18} className="text-secondary" />
              <span className='fw-medium text-muted fs-md'>Total Comments</span>
            </div>
            <span className="fw-semibold text-primary fs-md">
              {statsLoading ? '...' : postStats.total_comments.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      <PostModal
        isOpen={showPostModal}
        onClose={() => setShowPostModal(false)}
        onSubmit={handlePostSubmit}
        post={selectedPost}
        mediaType={selectedPost ? null : postMediaType}
        title={selectedPost ? "Edit Post" : "Create New Post"}
        size="lg"
        isLoading={isSubmitting}
      />

      <DeletePopup
        show={showDeletePopup}
        onHide={() => setShowDeletePopup(false)}
        onConfirm={handleDeletePost}
        title="Delete Post"
        content="Are you sure you want to delete this post? This action cannot be undone."
        saveTitle="Delete"
        disabled={isSubmitting}
      />

      <PostCommentsModal
        isOpen={showCommentsModal}
        onClose={() => {
          setShowCommentsModal(false);
          setSelectedPostId(null);
        }}
        postId={selectedPostId}
        title="Post Comments"
        size="lg"
      />

      <UserNetworkingModal
        isOpen={showLikesModal}
        onClose={() => {
          setShowLikesModal(false);
          setSelectedPostIdForLikes(null);
        }}
        userId={selectedPostIdForLikes}
        type="postLikes"
        className="networking-modal"
      />

      <PostView
        isOpen={showPostViewModal}
        onClose={() => {
          setShowPostViewModal(false);
          setSelectedPostForView(null);
        }}
        post={selectedPostForView}
        author={companyDetail}
        onLikesClick={(postId) => {
          setSelectedPostIdForLikes(postId);
          setShowLikesModal(true);
        }}
        onCommentsClick={(postId) => {
          setSelectedPostId(postId);
          setShowCommentsModal(true);
        }}
        size={selectedPostForView?.media?.length > 0 ? "xl" : "lg"}
        className="post-view-modal"
      />
    </div>
  );
};

export default Posts;
